/**
 * ChangeFrequencyModal - Multi-step wizard to change delivery frequency
 * Step 1: Select plan frequency (7, 14, or 28 days) - shows actual plan products
 * Step 2: Select meals for new plan
 * Step 3: Confirm price and update
 *
 * Pricing comes from hidden price products, but we display actual plan product images/titles
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Stack,
  Alert,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScaleIcon from "@mui/icons-material/Scale";
import InventoryIcon from "@mui/icons-material/Inventory";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { QuizButton } from "./shared/QuizButton";
import type { RechargeSubscription, ShopifyProduct, ShopifyVariant } from "../types";
import {
  getPackPricingConfig,
  toDailyKey50,
  type PlanDays,
} from "@/helpers/plansToPacks";
import {
  buildMealSelectionsWithBaseline,
  type MealSelectionWithBaseline,
} from "@/helpers/mealDistribution";
import { buildVariantIndex } from "../utils/recharge";
import { QUIZ } from "@/theme/quizTokens";

type ChangeFrequencyModalProps = {
  open: boolean;
  onClose: (success?: boolean) => void;
  subscription: RechargeSubscription | null;
  products: ShopifyProduct[];
  onConfirm: (
    newPlanDays: number,
    newPackSizeGrams: number,
    newVariantId: string,
    meals: MealSelection[],
    newPrice: number,
    mealsSummary: string
  ) => Promise<void>;
};

// Using MealSelectionWithBaseline from unified helper
type MealSelection = MealSelectionWithBaseline;

// Generate human-readable meals summary like "2× Salmon 800g, 3× Beef 800g"
function buildMealsSummary(meals: MealSelection[], packSize: number): string {
  return meals
    .filter((m) => m.count > 0)
    .map((m) => `${m.count}× ${m.productTitle} ${packSize}g`)
    .join(", ");
}

type Step = "frequency" | "meals" | "confirm";

type PlanProductInfo = {
  days: PlanDays;
  product: ShopifyProduct | null;
  hiddenPriceProduct: ShopifyProduct | null;
};

// Find the actual plan product (7 Day Plan, 14 Day Plan, 28 Day Plan)
function findPlanProduct(products: ShopifyProduct[], planDays: number): ShopifyProduct | null {
  return (
    products.find((p) => {
      const title = String(p.title || "").toLowerCase();
      const handle = String(p.handle || "").toLowerCase();
      // Match "7 day plan", "14 day plan", "28 day plan" but NOT hidden products
      const isHidden = handle.includes("hidden") || title.includes("hidden");
      if (isHidden) return false;
      return (
        title.includes(`${planDays} day plan`) ||
        title.includes(`${planDays}-day plan`) ||
        title.includes(`${planDays}day plan`)
      );
    }) || null
  );
}

// Find hidden price product for a plan (for pricing only)
function findHiddenPriceProduct(
  products: ShopifyProduct[],
  planDays: number
): ShopifyProduct | null {
  return (
    products.find((p) => {
      const handle = String(p.handle || "").toLowerCase();
      const title = String(p.title || "").toLowerCase();
      return (
        (handle.includes("hidden") && handle.includes(`${planDays}`)) ||
        (title.includes("hidden") && title.includes(`${planDays} day`))
      );
    }) || null
  );
}

// Get price for a pack size from hidden price product
function getPackPrice(hiddenProduct: ShopifyProduct | null, packSize: number): number {
  if (!hiddenProduct) return 0;

  const variant = hiddenProduct.variants?.find((v) => {
    const sizeFromTitle = parseInt(String(v.title || ""), 10);
    return sizeFromTitle === packSize;
  });

  return variant ? parseFloat(String(variant.price || "0")) : 0;
}

// Find meal/recipe products
function findMealProducts(products: ShopifyProduct[]): ShopifyProduct[] {
  return products.filter((p) => {
    const type = String(p.product_type || "").toLowerCase();
    const tags = String(p.tags || "").toLowerCase();
    return type === "recipe" || tags.includes("recipe") || tags.includes("meal");
  });
}

// findVariantForPackSize - helper to find variant matching a pack size
function findVariantForPackSize(
  product: ShopifyProduct,
  packSize: number
): ShopifyVariant | null {
  if (!product.variants?.length) return null;

  // Try to find variant with matching size in title (e.g., "800g" or "800")
  const matchingVariant = product.variants.find((v) => {
    const titleMatch = String(v.title || "").match(/(\d+)/);
    return titleMatch && parseInt(titleMatch[1], 10) === packSize;
  });

  // Fallback to first variant if no match
  return matchingVariant || product.variants[0];
}

// Get variant for bundle product (the actual plan product variant for Recharge)
function findBundleVariant(
  planProduct: ShopifyProduct | null,
  packSizeGrams: number
): ShopifyVariant | null {
  if (!planProduct?.variants?.length) return null;

  return (
    planProduct.variants.find((v) => {
      const sizeMatch = String(v.title || "").match(/(\d+)/);
      return sizeMatch && parseInt(sizeMatch[1], 10) === packSizeGrams;
    }) || null
  );
}

// Normalize image URLs (handle protocol-relative URLs)
function normalizeImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith("//")) return `https:${url}`;
  return url;
}

// Get product image URL with multiple fallbacks
function getProductImage(product: ShopifyProduct | null): string | null {
  if (!product) return null;

  // Try product.images array
  if (product.images?.[0]?.src) {
    return normalizeImageUrl(product.images[0].src);
  }

  // Try product.image as string
  if (typeof product.image === "string" && product.image) {
    return normalizeImageUrl(product.image);
  }

  // Try product.image as object with src
  if (product.image && typeof product.image === "object" && "src" in product.image) {
    return normalizeImageUrl((product.image as any).src);
  }

  // Try product.image as array
  if (Array.isArray(product.image) && product.image[0]) {
    const first = product.image[0];
    if (typeof first === "string") return normalizeImageUrl(first);
    if (first && typeof first === "object" && "src" in first) {
      return normalizeImageUrl((first as any).src);
    }
  }

  // Try variant images
  if (product.variants?.[0]) {
    const variant = product.variants[0] as any;
    if (variant.image?.src) return normalizeImageUrl(variant.image.src);
    if (variant.featured_image?.src) return normalizeImageUrl(variant.featured_image.src);
  }

  return null;
}

export function ChangeFrequencyModal({
  open,
  onClose,
  subscription,
  products,
  onConfirm,
}: ChangeFrequencyModalProps) {
  const [step, setStep] = useState<Step>("frequency");
  const [selectedPlanDays, setSelectedPlanDays] = useState<PlanDays>(7);
  const [mealSelections, setMealSelections] = useState<MealSelection[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Find plan products and hidden price products for each plan
  const planProducts = useMemo<PlanProductInfo[]>(() => {
    const plans: PlanDays[] = [7, 14, 28];
    return plans.map((days) => ({
      days,
      product: findPlanProduct(products, days),
      hiddenPriceProduct: findHiddenPriceProduct(products, days),
    }));
  }, [products]);

  // Build variant index for image lookup
  const variantIndex = useMemo(() => buildVariantIndex(products), [products]);

  // Extract current values from subscription
  const currentValues = useMemo(() => {
    if (!subscription?.properties) {
      return { dailyGrams: 300, packSizeGrams: 400, planDays: 7 as PlanDays };
    }

    const props = subscription.properties;

    // Get pack size
    const packSizeProp = props.find((p: any) => p.name === "_paws_pack_size_grams");
    const packSizeGrams = packSizeProp ? parseInt(packSizeProp.value, 10) : 400;

    // Get plan days
    const planDaysProp = props.find((p: any) => p.name === "_paws_plan_days");
    let planDays = planDaysProp ? parseInt(planDaysProp.value, 10) : 7;
    if (![7, 14, 28].includes(planDays)) planDays = 7;

    // Get daily grams from dog profile
    const dogJsonProp = props.find((p: any) => p.name === "_paws_dog_json");
    let dailyGrams = 300;
    if (dogJsonProp?.value) {
      try {
        const dogProfile = JSON.parse(dogJsonProp.value);
        dailyGrams = dogProfile.dailyGrams || dogProfile.daily_grams || 300;
      } catch {
        // Ignore parse errors
      }
    }

    return {
      dailyGrams,
      dailyKey: toDailyKey50(dailyGrams),
      packSizeGrams,
      planDays: planDays as PlanDays,
    };
  }, [subscription]);

  // Get selected plan info
  const selectedPlanInfo = useMemo(() => {
    return planProducts.find((p) => p.days === selectedPlanDays) || null;
  }, [planProducts, selectedPlanDays]);

  // Calculate new pack config based on selected plan days
  const newPackConfig = useMemo(() => {
    const { dailyKey, packSize, totalPacks } = getPackPricingConfig(
      currentValues.dailyGrams,
      selectedPlanDays
    );

    // Get price from hidden price product
    const hiddenProduct = selectedPlanInfo?.hiddenPriceProduct || null;
    const pricePerPack = getPackPrice(hiddenProduct, packSize);
    const totalPrice = totalPacks * pricePerPack;
    const pricePerDay = totalPrice / selectedPlanDays;

    // Find bundle variant from actual plan product (for Recharge variant ID)
    const bundleVariant = findBundleVariant(selectedPlanInfo?.product || null, packSize);

    return {
      dailyKey,
      packSize,
      totalPacks,
      pricePerPack,
      totalPrice,
      pricePerDay,
      bundleVariantId: bundleVariant ? String(bundleVariant.id) : null,
    };
  }, [selectedPlanDays, currentValues.dailyGrams, selectedPlanInfo]);

  // Current plan config for comparison
  const currentPackConfig = useMemo(() => {
    const { packSize, totalPacks } = getPackPricingConfig(
      currentValues.dailyGrams,
      currentValues.planDays
    );

    // Get price from hidden price product
    const currentPlanInfo = planProducts.find((p) => p.days === currentValues.planDays);
    const hiddenProduct = currentPlanInfo?.hiddenPriceProduct || null;
    const pricePerPack = getPackPrice(hiddenProduct, packSize);
    const totalPrice = totalPacks * pricePerPack;
    const pricePerDay = totalPrice / currentValues.planDays;

    return {
      packSize,
      totalPacks,
      totalPrice,
      pricePerDay,
    };
  }, [currentValues, planProducts]);

  // Meal products with variant info
  const mealProducts = useMemo(() => {
    return findMealProducts(products);
  }, [products]);

  // Total meals selected
  const totalMealsSelected = useMemo(() => {
    return mealSelections.reduce((sum, m) => sum + m.count, 0);
  }, [mealSelections]);

  // Calculate meal selection state (missing, perfect, extra)
  const mealState = useMemo(() => {
    const diff = totalMealsSelected - newPackConfig.totalPacks;
    if (diff < 0) {
      return {
        type: "missing" as const,
        icon: "warning",
        color: "#B20000",
        bg: "#FFF0F0",
        border: "#FFB3B3",
        message: `Please add ${Math.abs(diff)} more pack${Math.abs(diff) !== 1 ? "s" : ""} to match your plan`,
      };
    } else if (diff === 0) {
      return {
        type: "perfect" as const,
        icon: "check",
        color: "#2E7D32",
        bg: "#E5FFEA",
        border: "#A5D6A7",
        message: "Perfect! This is the recommended amount",
      };
    } else {
      return {
        type: "extra" as const,
        icon: "info",
        color: "#8A6E00",
        bg: "#FFF9E5",
        border: "#FFE8A3",
        message: `You're adding ${diff} extra pack${diff !== 1 ? "s" : ""} beyond the recommended ${newPackConfig.totalPacks}`,
      };
    }
  }, [totalMealsSelected, newPackConfig.totalPacks]);

  // Check if pack size changed
  const packSizeChanged = newPackConfig.packSize !== currentValues.packSizeGrams;
  const planChanged = selectedPlanDays !== currentValues.planDays;

  // Calculate extra meals price
  const extraMealsPrice = useMemo(() => {
    if (totalMealsSelected <= newPackConfig.totalPacks) return 0;
    const extraPacks = totalMealsSelected - newPackConfig.totalPacks;
    return extraPacks * newPackConfig.pricePerPack;
  }, [totalMealsSelected, newPackConfig.totalPacks, newPackConfig.pricePerPack]);

  const finalTotalPrice = newPackConfig.totalPrice + extraMealsPrice;

  // Track previous open state to only reset on actual modal open
  const prevOpenRef = useRef(false);

  // Reset state only when modal opens (not on data changes while open)
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    // Only reset when transitioning from closed to open
    if (open && !wasOpen) {
      setStep("frequency");
      setSelectedPlanDays(currentValues.planDays);
      setError(null);
      setSuccess(false);
      setConfirming(false);
      setMealSelections([]);
    }
  }, [open, currentValues.planDays]);

  const handleNext = () => {
    if (step === "frequency") {
      if (!planChanged) {
        setError("Please select a different delivery frequency");
        return;
      }
      // Auto-distribute meals evenly across all recipes using unified helper
      const distributed = buildMealSelectionsWithBaseline(
        mealProducts,
        newPackConfig.totalPacks,
        newPackConfig.packSize,
        findVariantForPackSize
      );
      setMealSelections(distributed);
      setError(null);
      setStep("meals");
    } else if (step === "meals") {
      if (totalMealsSelected < newPackConfig.totalPacks) {
        setError(`Please select at least ${newPackConfig.totalPacks} meals`);
        return;
      }
      setError(null);
      setStep("confirm");
    }
  };

  const handleBack = () => {
    setError(null);
    if (step === "meals") {
      setStep("frequency");
    } else if (step === "confirm") {
      setStep("meals");
    }
  };

  const handleMealChange = useCallback(
    (variantId: string, delta: number, product: ShopifyProduct, variant: ShopifyVariant) => {
      setMealSelections((prev) => {
        const existing = prev.find((m) => m.variantId === variantId);

        if (existing) {
          // Allow going to 0 (no minimum per meal - total just needs to meet recommended)
          const newCount = Math.max(0, existing.count + delta);
          return prev.map((m) =>
            m.variantId === variantId ? { ...m, count: newCount } : m
          );
        }

        // Only allow adding new items (delta > 0)
        if (delta > 0) {
          return [
            ...prev,
            {
              variantId,
              count: delta,
              baseline: 0,
              productTitle: product.title,
              variantTitle: variant.title || "",
              imageUrl: product.images?.[0]?.src || (product.image as any)?.src || null,
              price: parseFloat(String(variant.price || "0")),
            },
          ];
        }

        return prev;
      });
    },
    []
  );

  const handleConfirm = async () => {
    if (!newPackConfig.bundleVariantId) {
      setError("Unable to find bundle variant. Please contact support.");
      return;
    }

    if (!finalTotalPrice || finalTotalPrice <= 0) {
      setError("Unable to calculate pricing. Please contact us at support@pawsonplates.co.uk.");
      return;
    }

    const missingTitle = mealSelections.find(m => m.count > 0 && !m.productTitle);
    if (missingTitle) {
      setError("One of your selected meals is missing information. Please contact us at support@pawsonplates.co.uk.");
      return;
    }

    setConfirming(true);
    setError(null);

    try {
      const activeMeals = mealSelections.filter(m => m.count > 0);
      const mealsSummary = buildMealsSummary(activeMeals, newPackConfig.packSize);
      const mealsWithPrice = activeMeals.map(m => ({ ...m, price: newPackConfig.pricePerPack }));
      await onConfirm(
        selectedPlanDays,
        newPackConfig.packSize,
        newPackConfig.bundleVariantId,
        mealsWithPrice,
        finalTotalPrice,
        mealsSummary
      );
      setSuccess(true);
      setTimeout(() => {
        onClose(true);
      }, 1500);
    } catch (err: any) {
      setError(err?.message || "Failed to update delivery frequency");
    } finally {
      setConfirming(false);
    }
  };

  const formatPrice = (price: number) => `£${price.toFixed(2)}`;

  // Step indicator
  const steps = [
    { key: "frequency", label: "Select Frequency" },
    { key: "meals", label: "Select Meals" },
    { key: "confirm", label: "Confirm" },
  ];
  const currentStepIndex = steps.findIndex((s) => s.key === step);

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : () => onClose(false)}
      maxWidth={step === "confirm" ? "md" : "sm"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "16px" },
          maxHeight: { xs: "100%", sm: "90vh" },
          height: { xs: "100%", sm: "auto" },
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          minWidth: { xs: "100%", sm: 600 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: { xs: 2, md: 3 },
          borderBottom: "1px solid rgba(0,0,0,.08)",
          background: QUIZ.color.cream,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {step !== "frequency" && (
            <IconButton
              onClick={handleBack}
              disabled={confirming}
              size="small"
              sx={{ mr: 0.5 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #E8D6FF 0%, #CBA4FF 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 22, color: "#5B2E91" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 16, md: 18 },
                fontWeight: 800,
                color: QUIZ.color.brown,
              }}
            >
              {steps[currentStepIndex]?.label || "Change Frequency"}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 11, md: 12 },
                color: QUIZ.color.brown,
                opacity: 0.65,
              }}
            >
              Step {currentStepIndex + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={() => onClose(false)}
          disabled={confirming}
          sx={{
            color: QUIZ.color.brown,
            "&:hover": { background: "rgba(0,0,0,.05)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: { xs: 2, md: 3 } }}>
        {/* Success State */}
        {success && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{
              borderRadius: "12px",
              mb: 2,
              "& .MuiAlert-icon": { alignItems: "center" },
            }}
          >
            Delivery frequency updated successfully!
          </Alert>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{
              borderRadius: "12px",
              mb: 2,
              "& .MuiAlert-icon": { alignItems: "center" },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Step 1: Frequency Selection */}
        {step === "frequency" && !success && (
          <Box>
            <Typography
              sx={{ fontSize: 14, color: QUIZ.color.brown, opacity: 0.75, mb: 3 }}
            >
              Choose how often you'd like to receive your deliveries. Your daily
              serving ({currentValues.dailyKey}g) will stay the same.
            </Typography>

            {/* Plan Options - Show actual plan products */}
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {planProducts.map((planInfo) => {
                const { days, product, hiddenPriceProduct } = planInfo;
                const isSelected = selectedPlanDays === days;
                const isCurrent = currentValues.planDays === days;
                const productImage = getProductImage(product);
                const productTitle = product?.title || `${days} Day Plan`;

                // Calculate price for this plan using getPackPricingConfig
                const { packSize, totalPacks } = getPackPricingConfig(
                  currentValues.dailyGrams,
                  days
                );
                const pricePerPack = getPackPrice(hiddenPriceProduct, packSize);
                const totalPrice = totalPacks * pricePerPack;
                const pricePerDay = totalPrice / days;

                return (
                  <Box
                    key={days}
                    onClick={() => setSelectedPlanDays(days)}
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      border: isSelected
                        ? "2px solid #5B2E91"
                        : "1px solid rgba(0,0,0,.08)",
                      background: isSelected ? "#F8F0FF" : QUIZ.color.cream,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background: isSelected ? "#F8F0FF" : "#F0F0F0",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      {/* Product Image or Placeholder */}
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "12px",
                          overflow: "hidden",
                          flexShrink: 0,
                          border: isSelected ? "2px solid #5B2E91" : "1px solid rgba(0,0,0,.1)",
                          background: productImage ? "transparent" : QUIZ.color.cream,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {productImage ? (
                          <Box
                            component="img"
                            src={productImage}
                            alt={productTitle}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <InventoryIcon sx={{ fontSize: 28, color: isSelected ? "#5B2E91" : "#C4A98A" }} />
                        )}
                      </Box>

                      {/* Plan Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 700,
                              color: isSelected ? "#5B2E91" : QUIZ.color.brown,
                            }}
                          >
                            {productTitle}
                          </Typography>
                          {isCurrent && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: "#666",
                                px: 0.75,
                                py: 0.25,
                                background: QUIZ.color.cream3,
                                borderRadius: "4px",
                              }}
                            >
                              Current
                            </Typography>
                          )}
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 13,
                            color: QUIZ.color.brown,
                            opacity: 0.65,
                            mb: 0.5,
                          }}
                        >
                          Delivery every {days} days
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 18,
                              fontWeight: 800,
                              color: isSelected ? "#5B2E91" : QUIZ.color.brown,
                            }}
                          >
                            {formatPrice(totalPrice)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: "#666",
                            }}
                          >
                            ({formatPrice(pricePerDay)}/day)
                          </Typography>
                        </Box>
                      </Box>

                      {/* Selection indicator */}
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          border: isSelected
                            ? "2px solid #5B2E91"
                            : "2px solid #ccc",
                          background: isSelected ? "#5B2E91" : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {isSelected && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: QUIZ.color.warmWhite,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Selected plan details */}
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: QUIZ.color.cream,
                border: "1px solid rgba(0,0,0,.06)",
              }}
            >
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: QUIZ.color.brown, mb: 1.5 }}
              >
                Your {selectedPlanDays}-Day Plan will include:
              </Typography>

              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ScaleIcon sx={{ fontSize: 18, color: "#5B2E91" }} />
                    <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                      Pack size
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: QUIZ.color.brown }}>
                    {newPackConfig.packSize}g
                    {packSizeChanged && (
                      <Typography
                        component="span"
                        sx={{ fontSize: 12, color: "#666", ml: 1 }}
                      >
                        (was {currentValues.packSizeGrams}g)
                      </Typography>
                    )}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: "#5B2E91" }} />
                    <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                      Packs per delivery
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: QUIZ.color.brown }}>
                    {newPackConfig.totalPacks} packs
                    {planChanged && (
                      <Typography
                        component="span"
                        sx={{ fontSize: 12, color: "#666", ml: 1 }}
                      >
                        (was {currentPackConfig.totalPacks})
                      </Typography>
                    )}
                  </Typography>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                    Total per delivery
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#5B2E91" }}>
                    {formatPrice(newPackConfig.totalPrice)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 13, color: QUIZ.color.brown, opacity: 0.65 }}>
                    Per day
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: QUIZ.color.brown, opacity: 0.65 }}>
                    {formatPrice(newPackConfig.pricePerDay)}/day
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {packSizeChanged && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: "12px" }}>
                Pack size will change from {currentValues.packSizeGrams}g to{" "}
                {newPackConfig.packSize}g. You'll need to re-select your meals.
              </Alert>
            )}
          </Box>
        )}

        {/* Step 2: Meal Selection */}
        {step === "meals" && !success && (
          <Box>
            {/* Meal state banner */}
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: "12px",
                background: mealState.bg,
                border: `1px solid ${mealState.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: mealState.color,
                  textAlign: "center",
                }}
              >
                {mealState.message}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: 14, color: QUIZ.color.brown, opacity: 0.75 }}>
                Select your meals ({newPackConfig.totalPacks} recommended)
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "8px",
                  background:
                    mealState.type === "missing"
                      ? "#FFF3CD"
                      : mealState.type === "perfect"
                      ? "#E5FFEA"
                      : "#FFF9E5",
                  color:
                    mealState.type === "missing"
                      ? "#856404"
                      : mealState.type === "perfect"
                      ? "#2E7D32"
                      : "#8A6E00",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {totalMealsSelected} / {newPackConfig.totalPacks}
              </Box>
            </Box>

            <Stack spacing={1.5}>
              {mealProducts.map((product) => {
                const variant = findVariantForPackSize(product, newPackConfig.packSize);
                if (!variant) return null;

                const variantId = String(variant.id);
                const selection = mealSelections.find(
                  (m) => m.variantId === variantId
                );
                const count = selection?.count || 0;
                const canDecrement = count > 0;
                const imageUrl = variantIndex.get(variantId)?.imageUrl;

                return (
                  <Box
                    key={product.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "12px",
                      border: count > 0 ? "2px solid #5B2E91" : "1px solid rgba(0,0,0,.08)",
                      background: count > 0 ? "#F8F0FF" : QUIZ.color.cream,
                    }}
                  >
                    {/* Image */}
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "8px",
                        overflow: "hidden",
                        flexShrink: 0,
                        background: QUIZ.color.cream,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {imageUrl ? (
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={product.title}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <RestaurantIcon sx={{ fontSize: 24, color: "#C4A98A" }} />
                      )}
                    </Box>

                    {/* Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: QUIZ.color.brown,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {product.title}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: QUIZ.color.brown, opacity: 0.65 }}
                      >
                        {newPackConfig.packSize}g pack
                      </Typography>
                    </Box>

                    {/* Counter */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleMealChange(variantId, -1, product, variant)
                        }
                        disabled={!canDecrement}
                        sx={{
                          width: 32,
                          height: 32,
                          border: "1px solid rgba(0,0,0,.12)",
                          "&:disabled": { opacity: 0.3 },
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        sx={{
                          width: 32,
                          textAlign: "center",
                          fontWeight: 700,
                          fontSize: 16,
                        }}
                      >
                        {count}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleMealChange(variantId, 1, product, variant)
                        }
                        sx={{
                          width: 32,
                          height: 32,
                          background: "#5B2E91",
                          color: QUIZ.color.white,
                          "&:hover": { background: "#472172" },
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            {/* Extra meals price notice */}
            {extraMealsPrice > 0 && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: "12px" }}>
                Extra meals will add {formatPrice(extraMealsPrice)} to your order
              </Alert>
            )}
          </Box>
        )}

        {/* Step 3: Confirmation */}
        {step === "confirm" && !success && (
          <Box>
            <Typography
              sx={{ fontSize: 14, color: QUIZ.color.brown, opacity: 0.75, mb: 3 }}
            >
              Review your changes before confirming.
            </Typography>

            {/* Summary */}
            <Stack spacing={2}>
              {/* Frequency change */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: QUIZ.color.cream,
                  border: "1px solid rgba(0,0,0,.06)",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: QUIZ.color.brown, opacity: 0.65, mb: 1 }}
                >
                  DELIVERY FREQUENCY
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                    {currentValues.planDays} Day Plan → {selectedPlanDays} Day Plan
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#5B2E91",
                    }}
                  >
                    Every {selectedPlanDays} days
                  </Typography>
                </Box>
              </Box>

              {/* Pack size */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: QUIZ.color.cream,
                  border: "1px solid rgba(0,0,0,.06)",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: QUIZ.color.brown, opacity: 0.65, mb: 1 }}
                >
                  PACK DETAILS
                </Typography>
                <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                  {newPackConfig.packSize}g packs × {totalMealsSelected}
                  {totalMealsSelected > newPackConfig.totalPacks && (
                    <Typography
                      component="span"
                      sx={{ fontSize: 12, color: "#8A6E00", ml: 1 }}
                    >
                      (+{totalMealsSelected - newPackConfig.totalPacks} extra)
                    </Typography>
                  )}
                </Typography>
              </Box>

              {/* Meals */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: QUIZ.color.cream,
                  border: "1px solid rgba(0,0,0,.06)",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: QUIZ.color.brown, opacity: 0.65, mb: 1 }}
                >
                  MEALS ({totalMealsSelected} packs)
                </Typography>
                <Stack spacing={0.5}>
                  {mealSelections.map((m) => (
                    <Typography
                      key={m.variantId}
                      sx={{ fontSize: 13, color: QUIZ.color.brown }}
                    >
                      {m.count}× {m.productTitle}
                    </Typography>
                  ))}
                </Stack>
              </Box>

              {/* Price */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: "#F8F0FF",
                  border: "2px solid #D4B8FF",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: "#5B2E91", mb: 1 }}
                >
                  NEW PRICE
                </Typography>
                {extraMealsPrice > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: 13, color: QUIZ.color.brown, opacity: 0.75 }}>
                        Base plan ({newPackConfig.totalPacks} packs)
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: QUIZ.color.brown }}>
                        {formatPrice(newPackConfig.totalPrice)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: 13, color: "#8A6E00" }}>
                        Extra packs ({totalMealsSelected - newPackConfig.totalPacks})
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#8A6E00" }}>
                        +{formatPrice(extraMealsPrice)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                  </Box>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                    Per delivery
                  </Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#5B2E91" }}>
                    {formatPrice(finalTotalPrice)}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: 12, color: QUIZ.color.brown, opacity: 0.65, textAlign: "right" }}
                >
                  ({formatPrice(finalTotalPrice / selectedPlanDays)}/day)
                </Typography>
              </Box>

              {/* Comparison */}
              {currentPackConfig.totalPrice > 0 && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "8px",
                    background: finalTotalPrice > currentPackConfig.totalPrice ? "#FFF9E5" : "#E5FFEA",
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: QUIZ.color.brown }}>
                    {finalTotalPrice > currentPackConfig.totalPrice
                      ? `This is ${formatPrice(finalTotalPrice - currentPackConfig.totalPrice)} more per delivery`
                      : finalTotalPrice < currentPackConfig.totalPrice
                      ? `You'll save ${formatPrice(currentPackConfig.totalPrice - finalTotalPrice)} per delivery`
                      : "Same price per delivery"}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Footer */}
      {!success && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: "1px solid rgba(0,0,0,.08)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <QuizButton
              variant="secondary"
              onClick={step === "frequency" ? () => onClose(false) : handleBack}
              disabled={confirming}
              fullWidth
            >
              {step === "frequency" ? "Cancel" : "Back"}
            </QuizButton>
            <QuizButton
              variant="primary"
              onClick={step === "confirm" ? handleConfirm : handleNext}
              disabled={
                confirming ||
                (step === "frequency" && !planChanged) ||
                (step === "meals" && totalMealsSelected < newPackConfig.totalPacks)
              }
              fullWidth
            >
              {confirming
                ? "Updating..."
                : step === "confirm"
                ? "Confirm Changes"
                : "Next"}
            </QuizButton>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
